import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BuildingOfficeIcon, PencilIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { getLocalApiUrl, authFetch } from '../../config/api';
import {
  defaultPolicyForTier,
  ensureVenuePolicy,
  formatCurrency,
  formatTierRange as formatRange
} from './payoutPolicyUtils';

function venueRowMatchesEditKey(v, key) {
  if (v == null || key == null) return false
  const k = String(key)
  return String(v.id) === k || String(v.venue_id) === k
}

export default function PayoutRulesBuilder() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingVenueId, setEditingVenueId] = useState(null);
  const [activeTierId, setActiveTierId] = useState('PV1');
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [newVenue, setNewVenue] = useState({ name: '', code: '' });
  const [staffRoles, setStaffRoles] = useState([]);
  const [validationError, setValidationError] = useState(null);

  // Staff roles for PIN / issuer / chain selection — from the staff_roles lookup.
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/staff/roles/all'));
        const j = await r.json();
        if (j.success && Array.isArray(j.data)) {
          setStaffRoles(
            j.data
              .filter((x) => x.active !== false)
              .sort((a, b) => (a.rank - b.rank) || String(a.label).localeCompare(String(b.label)))
              .map((x) => ({ id: x.role_key, name: x.label }))
          );
        }
      } catch (_) { /* roles list stays empty; selectors fall back to role keys */ }
    })();
  }, []);

  /** Signer options include the Player (patron sentinel); PIN/issuer are staff-only. */
  const signatureRoles = useMemo(() => [{ id: 'patron', name: 'Player' }, ...staffRoles], [staffRoles]);
  const roleNameById = useCallback(
    (id) => (id === 'patron' ? 'Player' : (staffRoles.find((r) => r.id === id)?.name || id || '—')),
    [staffRoles]
  );

  const editingVenue = useMemo(
    () => venues.find((v) => venueRowMatchesEditKey(v, editingVenueId)) || null,
    [venues, editingVenueId]
  );

  const loadVenues = useCallback(async (opts = {}) => {
    const quiet = !!opts.quiet
    try {
      if (!quiet) setLoading(true);
      const r = await authFetch(getLocalApiUrl('/api/venues'));
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to load venues');
      const list = (j.data || []).map(v => {
        let policy = {};
        if (v.payout_policy) {
          try { policy = typeof v.payout_policy === 'object' ? v.payout_policy : JSON.parse(v.payout_policy); } catch (_) {}
        }
        return { id: v.id, venue_id: v.venue_id, name: v.name, code: v.code, payoutPolicy: policy };
      });
      setVenues(list.map(v => ensureVenuePolicy(v)));
    } catch (e) {
      console.error('Load venues failed:', e);
      setVenues([]);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { loadVenues(); }, [loadVenues]);

  const loadPolicyForVenue = useCallback(async (venueId) => {
    try {
      const r = await authFetch(getLocalApiUrl(`/api/venues/${venueId}/payout-policy`));
      const j = await r.json();
      if (j.success && j.data) {
        const policy = j.data.payoutPolicy ?? {};
        setVenues((prev) =>
          prev.map((v) =>
            venueRowMatchesEditKey(v, venueId)
              ? ensureVenuePolicy({ ...v, payoutPolicy: policy })
              : v
          )
        );
      }
    } catch (_) {}
  }, []);

  // Validate policy before saving
  const validatePolicy = useCallback(() => {
    if (!editingVenue) return null;
    const policy = editingVenue.payoutPolicy || {};
    for (const tierId of ['PV1', 'PV2', 'PV3', 'PV4']) {
      const tier = policy[tierId];
      if (!tier) continue;
      const minAmt = tier.minAmount ?? 0;
      const maxAmt = tier.maxAmount;
      // Check for negative values
      if (minAmt < 0) {
        return `${tierId}: Min Amount cannot be negative`;
      }
      if (maxAmt != null && maxAmt < 0) {
        return `${tierId}: Max Amount cannot be negative`;
      }
      // Check Max >= Min
      if (maxAmt != null && maxAmt < minAmt) {
        return `${tierId}: Max Amount ($${maxAmt.toLocaleString()}) must be greater than or equal to Min Amount ($${minAmt.toLocaleString()})`;
      }
    }
    return null;
  }, [editingVenue]);

  const savePolicy = useCallback(async () => {
    if (!editingVenue) return;
    
    // Validate before saving
    const error = validatePolicy();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    
    const venueId = editingVenue.venue_id || editingVenue.id;
    try {
      setSaving(true);
      const r = await authFetch(getLocalApiUrl(`/api/venues/${venueId}/payout-policy`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payoutPolicy: Object.fromEntries(
            Object.entries(editingVenue.payoutPolicy || {}).map(([tierId, tierPolicy]) => [
              tierId,
              { ...tierPolicy, authorizationChain: [] },
            ])
          ),
        })
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to save');
      const savedPolicy = j.data?.payoutPolicy ?? editingVenue.payoutPolicy
      setVenues((prev) =>
        prev.map((v) =>
          venueRowMatchesEditKey(v, editingVenueId)
            ? ensureVenuePolicy({ ...v, payoutPolicy: savedPolicy })
            : v
        )
      );
      setEditingVenueId(null);
      await loadVenues({ quiet: true });
    } catch (e) {
      console.error('Save policy failed:', e);
      alert(e.message || 'Failed to save payout policy');
    } finally {
      setSaving(false);
    }
  }, [editingVenue, editingVenueId, loadVenues, validatePolicy]);

  const updatePolicyField = (tierId, field, value) => {
    setVenues(prev => prev.map(v => {
      if (!venueRowMatchesEditKey(v, editingVenueId)) return v;
      const withPolicy = ensureVenuePolicy(v);
      return {
        ...withPolicy,
        payoutPolicy: {
          ...withPolicy.payoutPolicy,
          [tierId]: { ...withPolicy.payoutPolicy[tierId], [field]: value }
        }
      };
    }));
  };

  const togglePolicyArray = (tierId, field, roleId) => {
    setVenues(prev => prev.map(v => {
      if (!venueRowMatchesEditKey(v, editingVenueId)) return v;
      const withPolicy = ensureVenuePolicy(v);
      const current = new Set(withPolicy.payoutPolicy[tierId][field] || []);
      if (current.has(roleId)) current.delete(roleId); else current.add(roleId);
      return {
        ...withPolicy,
        payoutPolicy: {
          ...withPolicy.payoutPolicy,
          [tierId]: { ...withPolicy.payoutPolicy[tierId], [field]: Array.from(current) }
        }
      };
    }));
  };

  const toggleTierEnabled = (venueId, tierId) => {
    setVenues(prev => prev.map(v => {
      if (!venueRowMatchesEditKey(v, venueId)) return v;
      const withPolicy = ensureVenuePolicy(v);
      const current = !!withPolicy.payoutPolicy[tierId].enabled;
      return {
        ...withPolicy,
        payoutPolicy: {
          ...withPolicy.payoutPolicy,
          [tierId]: { ...withPolicy.payoutPolicy[tierId], enabled: !current }
        }
      };
    }));
  };

  const addVenue = () => {
    const name = newVenue.name.trim();
    const code = newVenue.code.trim();
    if (!name || !code) return;
    const v = ensureVenuePolicy({ id: Date.now(), venue_id: code.toLowerCase().replace(/\s+/g, '_'), name, code });
    setVenues(prev => [ ...prev, v ]);
    setShowAddVenue(false);
    setNewVenue({ name: '', code: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-psg-teal" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Payout Rules Builder</h2>
        <p className="text-sm text-gray-600">Select a venue below to edit its payout policy (PV1–PV4). Rules can be venue-specific or shared across venues.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50">
          <div className="text-sm font-medium text-gray-700">Venues</div>
          <button
            onClick={() => setShowAddVenue(true)}
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-psg-teal text-white hover:bg-psg-teal-600"
          >
            <PlusIcon className="h-4 w-4 mr-1" /> Add Venue
          </button>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PV1</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PV2</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PV3</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PV4</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {venues.map(v => (
              <tr key={v.id ?? v.venue_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900 flex items-center gap-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" /> {v.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{v.code}</td>
                {['PV1','PV2','PV3','PV4'].map(tid => (
                  <td key={`${v.id ?? v.venue_id}-${tid}`} className="px-6 py-4 text-center align-top">
                    {(() => {
                      const policy = ensureVenuePolicy(v).payoutPolicy[tid];
                      const issuer = policy.issuerRoleId ? roleNameById(policy.issuerRoleId) : '—';
                      return (
                        <div className="inline-flex flex-col items-center gap-1">
                          <input
                            type="checkbox"
                            checked={!!policy.enabled}
                            onChange={() => toggleTierEnabled(v.id ?? v.venue_id, tid)}
                            className="h-4 w-4 text-psg-teal focus:ring-psg-teal border-gray-300 rounded"
                          />
                          <div className="text-[11px] text-gray-700 font-medium">{formatRange(policy.minAmount, policy.maxAmount)}</div>
                          <div className="text-[10px] text-gray-600">{String(policy.cashDispensed).replaceAll('_',' ')}</div>
                          <div className="text-[10px] text-gray-500">Issuer: {issuer}</div>
                          <div className="text-[10px] text-gray-400">KYC: {policy.kycRequired ? '✓' : '—'} • Occ: {policy.occupationRequired ? '✓' : '—'} • Sig: {policy.signaturesRequired ?? 0}</div>
                        </div>
                      );
                    })()}
                  </td>
                ))}
                <td className="px-6 py-4 text-sm">
                  <button
                    onClick={() => {
                      const key = v.id ?? v.venue_id;
                      setEditingVenueId(key);
                      setActiveTierId('PV1');
                      setValidationError(null);
                      loadPolicyForVenue(key);
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-psg-teal text-white hover:bg-psg-teal-600"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" /> Edit Rules
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddVenue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Venue</h3>
              <button onClick={() => setShowAddVenue(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Venue Name</label>
                <input
                  type="text"
                  value={newVenue.name}
                  onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                  className="w-full border-gray-300 rounded-md text-sm"
                  placeholder="PaySafeGO New Venue"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Code</label>
                <input
                  type="text"
                  value={newVenue.code}
                  onChange={(e) => setNewVenue({ ...newVenue, code: e.target.value })}
                  className="w-full border-gray-300 rounded-md text-sm"
                  placeholder="PSX"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAddVenue(false)} className="px-3 py-1.5 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
              <button onClick={addVenue} className="px-3 py-1.5 rounded-md bg-psg-teal text-white hover:bg-psg-teal-600">Add Venue</button>
            </div>
          </div>
        </div>
      )}

      {editingVenue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto w-11/12 max-w-5xl bg-white rounded-md shadow-lg">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{editingVenue.name} — Payout Policy</h3>
                <p className="text-xs text-gray-500">Configure PV1–PV4 rules for this venue</p>
              </div>
              <button onClick={() => setEditingVenueId(null)} className="p-2 text-gray-600 hover:text-gray-800">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex">
              {/* Left: PV Nav */}
              <div className="w-40 border-r p-4 space-y-2">
                {['PV1','PV2','PV3','PV4'].map(tid => (
                  <button
                    key={tid}
                    onClick={() => setActiveTierId(tid)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm ${activeTierId===tid ? 'bg-psg-teal text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                  >
                    {tid}
                  </button>
                ))}
              </div>
              {/* Right: Fields */}
              <div className="flex-1 p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Amount ($)</label>
                    <input
                      type="number"
                      min={0}
                      value={editingVenue.payoutPolicy[activeTierId].minAmount ?? 0}
                      onChange={e => {
                        const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                        updatePolicyField(activeTierId, 'minAmount', Math.max(0, val || 0));
                        setValidationError(null);
                      }}
                      onKeyDown={e => {
                        // Block alphabets, negative sign, and special characters except navigation keys
                        if (!/[\d]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      className="w-full border-gray-300 rounded-md text-sm"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Amount ($)</label>
                    <input
                      type="number"
                      min={0}
                      value={editingVenue.payoutPolicy[activeTierId].maxAmount ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        updatePolicyField(activeTierId, 'maxAmount', val != null ? Math.max(0, val) : null);
                        setValidationError(null);
                      }}
                      onKeyDown={e => {
                        // Block alphabets, negative sign, and special characters except navigation keys
                        if (!/[\d]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="No limit"
                      className="w-full border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">KYC Required</label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!editingVenue.payoutPolicy[activeTierId].kycRequired}
                        onChange={e => updatePolicyField(activeTierId, 'kycRequired', e.target.checked)}
                        className="h-4 w-4 text-psg-teal focus:ring-psg-teal border-gray-300 rounded"
                      />
                      <span>Patron must have verified KYC for payouts in this tier</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Require Occupation</label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!editingVenue.payoutPolicy[activeTierId].occupationRequired}
                        onChange={e => updatePolicyField(activeTierId, 'occupationRequired', e.target.checked)}
                        className="h-4 w-4 text-psg-teal focus:ring-psg-teal border-gray-300 rounded"
                      />
                      <span>Occupation must be captured for this tier</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Cash Method</label>
                    <select
                      value={editingVenue.payoutPolicy[activeTierId].cashDispensed}
                      onChange={e => updatePolicyField(activeTierId, 'cashDispensed', e.target.value)}
                      className="w-full border-gray-300 rounded-md text-sm"
                    >
                      <option value="CRT_OR_CASH">CRT OR CASH</option>
                      <option value="CASH_ONLY">CASH ONLY</option>
                      <option value="NO_CASH">NO-CASH</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Issuer</label>
                    <select
                      value={editingVenue.payoutPolicy[activeTierId].issuerRoleId || ''}
                      onChange={e => updatePolicyField(activeTierId, 'issuerRoleId', e.target.value || null)}
                      className="w-full border-gray-300 rounded-md text-sm"
                    >
                      {staffRoles.map(r => (<option key={r.id} value={r.id}>{r.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Signatures Required</label>
                    <input
                      type="number"
                      min={0}
                      max={5}
                      value={editingVenue.payoutPolicy[activeTierId].signaturesRequired ?? 0}
                      onChange={e => updatePolicyField(activeTierId, 'signaturesRequired', parseInt(e.target.value || '0', 10))}
                      className="w-full border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Signer Roles</label>
                    <p className="text-[11px] text-gray-500 mb-2">Roles that may sign when signatures are required. Use Signatures Required above to set how many signatures are needed.</p>
                    <div className="flex flex-wrap gap-2">
                      {signatureRoles.map(r => {
                        const selected = new Set(editingVenue.payoutPolicy[activeTierId].signerRoleIds || []);
                        return (
                          <label key={r.id} className="inline-flex items-center gap-2 text-xs border rounded px-2 py-1">
                            <input type="checkbox" checked={selected.has(r.id)} onChange={() => togglePolicyArray(activeTierId, 'signerRoleIds', r.id)} />
                            {r.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {validationError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-700">{validationError}</p>
                  </div>
                )}
                <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                  <button onClick={() => { setEditingVenueId(null); setValidationError(null); }} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Close</button>
                  <button onClick={savePolicy} disabled={saving} className="px-4 py-2 bg-psg-teal text-white rounded hover:bg-psg-teal-600 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}